import React, { useEffect, useState } from "react";
import { api, friendlyError } from "../lib/api";
import { PageHeader } from "../components/AppLayout";
import { Pill } from "../lib/ui";
import { Building2, Cpu, Beaker, Droplets, ShieldCheck, Edit3, Save, X } from "lucide-react";
import { toast } from "sonner";

const TECH_OPTIONS = ["aseptic filling", "terminal sterilisation", "lyophilisation", "isolator", "rabs",
  "crabs", "autoclave", "vhp", "bfs", "single-use systems", "wfi", "pure steam", "biologics", "adc", "hpapi"];
const PROD_OPTIONS = ["sterile injectable", "lyophilised", "oral solid", "api", "biologic ds", "biologic dp", "adc"];
const MARKET_OPTIONS = ["usa", "eu", "japan", "china", "korea", "taiwan", "turkey", "eaeu"];

function TreeNode({ label, children, icon: Icon, level = 0 }) {
  return (
    <div style={{ marginLeft: level * 16 }} className="border-l border-slate-200 pl-3 py-1">
      <div className="flex items-center gap-2 text-sm">
        {Icon && <Icon size={14} className="text-blue-600" />}<span className="font-medium">{label}</span>
      </div>
      {children}
    </div>
  );
}

function MultiSelect({ label, options, selected, onChange, testid }) {
  return (
    <div>
      <div className="ri-overline mb-2">{label}</div>
      <div className="flex flex-wrap gap-1.5" data-testid={testid}>
        {options.map((o) => {
          const on = selected.includes(o);
          return (
            <button key={o} type="button" onClick={() => onChange(on ? selected.filter((x) => x !== o) : [...selected, o])}
              className={`px-2.5 py-1 rounded-md text-xs font-medium border transition-colors ${
                on ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200 hover:border-blue-400"}`}>
              {o}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function Sito() {
  const [site, setSite] = useState(null);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState(null);

  const load = () => api.get("/sites").then((r) => {
    const s = r.data.find((x) => x.is_primary) || r.data[0];
    setSite(s); setForm(s);
  });
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.put(`/sites/${site.id}`, {
        name: form.name, country: form.country, city: form.city,
        departments: form.departments || [], technologies: form.technologies || [],
        product_categories: form.product_categories || [], markets: form.markets || [],
        utilities: form.utilities || [], quality_systems: form.quality_systems || [],
        is_primary: form.is_primary,
      });
      toast.success("Profilo sito aggiornato");
      setEdit(false); load();
    } catch (e) { toast.error(friendlyError(e)); }
  };

  if (!site) return <div className="text-slate-400">Caricamento...</div>;
  const d = edit ? form : site;

  return (
    <div className="ri-fade-in max-w-5xl">
      <PageHeader overline="Digital Regulatory Profile" title="Il mio sito"
        subtitle="Il profilo del sito alimenta automaticamente l'Applicability Engine e gli impact assessment."
        actions={edit ? (
          <div className="flex gap-2">
            <button onClick={save} data-testid="site-save-btn" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-blue-700 text-white text-sm hover:bg-blue-800"><Save size={15} /> Salva</button>
            <button onClick={() => { setEdit(false); setForm(site); }} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-300 text-sm"><X size={15} /> Annulla</button>
          </div>
        ) : (
          <button onClick={() => setEdit(true)} data-testid="site-edit-btn" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-300 text-sm hover:bg-slate-50"><Edit3 size={15} /> Modifica</button>
        )} />

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Hierarchy tree */}
        <div className="rounded-xl bg-white border border-slate-200 p-5">
          <h3 className="font-head font-semibold text-sm mb-3">Gerarchia</h3>
          <TreeNode label="Sterivax CDMO (Org)" icon={Building2}>
            <TreeNode label={d.name} icon={Building2} level={1}>
              {(d.departments || []).map((dep) => <TreeNode key={dep} label={dep} icon={Cpu} level={2} />)}
            </TreeNode>
          </TreeNode>
        </div>

        {/* Details */}
        <div className="lg:col-span-2 space-y-5">
          <div className="rounded-xl bg-white border border-slate-200 p-5 space-y-4">
            {edit ? (
              <div className="grid sm:grid-cols-3 gap-3">
                <input value={d.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nome sito"
                  className="px-3 py-2 rounded-lg border border-slate-200 text-sm sm:col-span-3" data-testid="site-name-input" />
                <input value={d.city || ""} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Città"
                  className="px-3 py-2 rounded-lg border border-slate-200 text-sm" />
                <input value={d.country || ""} onChange={(e) => setForm({ ...form, country: e.target.value })} placeholder="Paese"
                  className="px-3 py-2 rounded-lg border border-slate-200 text-sm" />
              </div>
            ) : (
              <div>
                <h3 className="font-head font-semibold">{d.name}</h3>
                <p className="text-sm text-slate-500">{d.city}, {d.country}</p>
              </div>
            )}
            {edit ? (
              <MultiSelect label="Manufacturing Technologies" options={TECH_OPTIONS} selected={d.technologies || []}
                onChange={(v) => setForm({ ...form, technologies: v })} testid="site-tech-select" />
            ) : (
              <div>
                <div className="ri-overline mb-2 flex items-center gap-1"><Cpu size={12} /> Tecnologie</div>
                <div className="flex flex-wrap gap-1.5">{(d.technologies || []).map((t) => <Pill key={t} color="#1E40AF" bg="#DBEAFE">{t}</Pill>)}</div>
              </div>
            )}
            {edit ? (
              <MultiSelect label="Categorie di prodotto" options={PROD_OPTIONS} selected={d.product_categories || []}
                onChange={(v) => setForm({ ...form, product_categories: v })} testid="site-prod-select" />
            ) : (
              <div>
                <div className="ri-overline mb-2 flex items-center gap-1"><Beaker size={12} /> Prodotti</div>
                <div className="flex flex-wrap gap-1.5">{(d.product_categories || []).map((t) => <Pill key={t}>{t}</Pill>)}</div>
              </div>
            )}
            {edit ? (
              <MultiSelect label="Mercati" options={MARKET_OPTIONS} selected={d.markets || []}
                onChange={(v) => setForm({ ...form, markets: v })} testid="site-market-select" />
            ) : (
              <div>
                <div className="ri-overline mb-2">Mercati</div>
                <div className="flex flex-wrap gap-1.5">{(d.markets || []).map((t) => <Pill key={t} color="#166534" bg="#DCFCE7">{t}</Pill>)}</div>
              </div>
            )}
          </div>

          {!edit && (
            <div className="grid sm:grid-cols-2 gap-5">
              <div className="rounded-xl bg-white border border-slate-200 p-5">
                <div className="ri-overline mb-2 flex items-center gap-1"><Droplets size={12} /> Utilities</div>
                <div className="flex flex-wrap gap-1.5">{(d.utilities || []).map((t) => <Pill key={t}>{t}</Pill>)}</div>
              </div>
              <div className="rounded-xl bg-white border border-slate-200 p-5">
                <div className="ri-overline mb-2 flex items-center gap-1"><ShieldCheck size={12} /> Quality systems</div>
                <div className="flex flex-wrap gap-1.5">{(d.quality_systems || []).map((t) => <Pill key={t}>{t}</Pill>)}</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
